import React, { createContext, createRef, useCallback, useEffect, useRef } from 'react';
import DebugData from './DebugData';
import Result from './Result/Result';
import SpcData from './TestPages/SpcData';
import SelectorTagBar from './SelectorTagBar';
import LoadData from './OperationalData/LoadData';
import GearboxInputData from './OperationalData/GearboxData';
import StartingConstraint from './OperationalData/StartingConstraint';
import Loader from '../common/Loader';
import KeyVisual from '../common/KeyVisual';
import ContentContainer from '../common/ContentContainer';
import Section, { SectionColor } from '../common/Section';
import motorImage from '../../../assets/images/motor.png';
import Flex, { FlexDirection, FlexJustification } from '../common/Flex';
import { useHook } from '../../hooks';
import { useAppDispatch, useAppSelector, useTranslate } from '../../hooks/common';
import { DEFAULT_FREQUENCY, INITIAL_CHART_IMAGES, TRANSLATIONS } from '../../constants';
import DiagramCarousel, { DocumentImageHandle } from './Diagram/DiagramCarousel';
import { removeTags, setClientMotorResponse, setDisableOperationalData, setLanguage, setLoadInertiaHighDialog, setMesRequest, setNumberOfStartupsDialog, setRenderConverterOperation, setRenderLineOperations, setVsdOperationType, toggleLoading, toggleUpdateFilters } from '../../store';
import { Api, apiCall, getDefaultFilter, getDefaultLanguage, getFormData, saveDocument, track, showInfoModal } from '../../services';
import { Language, LoadType, MotorDriveConnection, StartingCondition, StartingType, VsdOperationType } from '../../enums';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Tab } from '../common/TabSwitcher';
import { InnoIcon, showStatusMessage } from '@innomotics/innomotics-build-react-lib';

interface HomeProps {
    debug: boolean;
    spcRequest?: boolean;
    ipcRequest?: boolean;
}

export interface HomeContext {
    getMainBoundary?: () => DOMRect | undefined;
}

export const HomeContext = createContext<HomeContext>({});

const Home = ({ debug, spcRequest, ipcRequest }: HomeProps) => {
    const loading = useAppSelector(state => state.motor.loading);
    const hasMotor = useAppSelector(state => state.motor.hasMotor);
    const navigate = useNavigate();
    const translate = useTranslate();
    const dispatch = useAppDispatch();
    const mesRequest = useAppSelector(state => state.motor.mesRequest);
    const calculationResponse = useAppSelector(state => state.motor.calculationResponse);
    const clientMotorResponse = useAppSelector(state => state.motor.clientMotorResponse);
    const calculationSuccessful = useAppSelector(state => state.motor.calculationSuccessful);
    const renderLineOperations = useAppSelector(state => state.motor.renderLineOperations);
    const renderConverterOperation = useAppSelector(state => state.motor.renderConverterOperation);
    const disableOperationalData = useAppSelector(state => state.motor.disableOperationalData);
    const showLoadInertiaHighDialog = useAppSelector(state => state.load.showLoadInertiaHighDialog);
    const showNumberOfStartupsDialog = useAppSelector(state => state.motor.showNumberOfStartupsDialog);
    const disabledCurveTypes = useAppSelector(state => state.diagram.disabledCurveTypes);
    const filter = useAppSelector(state => state.motor.filter);
    const language = useAppSelector(state => state.translation.language);
    const chartIndex = useAppSelector(state => state.diagram.chartIndex);
    const diagramRef = createRef<DocumentImageHandle>();
    const mainRef = useRef<HTMLDivElement>(null);
    const isDolOperation = calculationResponse?.mesParameters.inputData.vsdMotorData.isDolOperation;
    const isVsdOperation = calculationResponse?.mesParameters.inputData.vsdMotorData.isVsdOperation;
    const disableDocumentCreation = (clientMotorResponse ? false : !hasMotor) || !calculationSuccessful || (calculationResponse && !calculationResponse.isStartupPossible);
    const [params] = useSearchParams();
    const { onFilterChange, loadInitialData, loadInitialDtkData, getCalculationData } = useHook(x => x.filter)();

    useEffect(() => {
        if (spcRequest) {
            // Call SPC and a restored session already supply the motor data.
            // Do not overwrite them with a request made from an empty query string.
            if (!clientMotorResponse && params.toString()) getClientMotorResponse();
        }
        else if (ipcRequest) {
            getClientMotorResponseIpc();
        } else if (!clientMotorResponse) {
            loadInitialData();
        }
    }, []);

    useEffect(() => {
        if (clientMotorResponse && (!spcRequest || !calculationResponse)) {
            loadInitialDtkData();
        }
    }, [clientMotorResponse, mesRequest]);

    useEffect(() => {
        (hasMotor || clientMotorResponse) && trackDiagramSelection();
    }, [hasMotor, clientMotorResponse, mesRequest]);

    useEffect(() => {
        trackDiagramSelection();
    }, [chartIndex]);

    useEffect(() => {
        if (showLoadInertiaHighDialog) {
            renderLoadInertiaDialog();
        }
        if (showNumberOfStartupsDialog) {
            renderNumberOfStartupsDialog();
        }
    }, [showLoadInertiaHighDialog, showNumberOfStartupsDialog]);

    const getTabs = (englishLabels = false) => {
        const tabs: Tab[] = [];
        const languageId = englishLabels ? Language.En : language;

        renderLineOperations &&
            tabs.push(
                { label: translate(TRANSLATIONS.diagram.thumbnail.torqueOverSpeed, undefined, languageId) },
                { label: translate(TRANSLATIONS.diagram.thumbnail.currentOverSpeed, undefined, languageId) },
                { label: translate(TRANSLATIONS.diagram.thumbnail.powerOverSpeed, undefined, languageId) },
                { label: translate(TRANSLATIONS.diagram.thumbnail.timeOverSpeed, undefined, languageId) },
                { label: translate(TRANSLATIONS.diagram.thumbnail.currentOverTime, undefined, languageId) }
            );

        renderConverterOperation &&
            tabs.push({ label: <>{translate(TRANSLATIONS.diagram.thumbnail.converterOperationVsd, undefined, languageId)} <span className='vsd-mark'>VSD</span></> });

        return tabs;
    };

    const trackDiagramSelection = () => {
        const selectedChartLabel = getTabs(true)[chartIndex];
        const { mlfb, startingType } = mesRequest.commonData;

        if (mlfb && selectedChartLabel) {
            track('DiagramSelection', {
                mlfb: mlfb,
                name: selectedChartLabel.label,
                startingType: StartingType[startingType]
            });
        }
    };

    const getClientMotorResponse = async () => {
        dispatch(toggleLoading());

        const spcMotorRequest = Array.from(params.keys()).reduce(
            (acc, val) => ({ ...acc, [val]: params.get(val) }),
            {}
        );

        const formData = getFormData(spcMotorRequest);

        await apiCall(
            Api.getConfiguration(formData),
            async x => {
                const clientMotorRequest = JSON.parse(`${x.data.clientMotorRequest}`);

                dispatch(setMesRequest(x.data.motorRequest));
                dispatch(setClientMotorResponse({
                    motorRequest: x.data.motorRequest,
                    clientMotorRequest
                }));
                dispatch(setLanguage(getDefaultLanguage(clientMotorRequest.JOM_LANG || clientMotorRequest.LANG)));
            },
            async () => { }
        );

        dispatch(toggleLoading());
    };

    const getClientMotorResponseIpc = async () => {
        dispatch(toggleLoading());

        await apiCall(
            Api.getConfigurationIpc(),
            async x => {
                const clientMotorRequest = JSON.parse(`${x.data.clientMotorRequest}`);

                dispatch(setMesRequest(x.data.motorRequest));
                dispatch(setClientMotorResponse({
                    motorRequest: x.data.motorRequest,
                    clientMotorRequest
                }));
                dispatch(setLanguage(getDefaultLanguage(clientMotorRequest.JOM_LANG || clientMotorRequest.LANG)));

                // Keep the IPC result on the calculation/results page. The root route
                // redirects to the manual test page in non-production environments.
                navigate('/results');
            },
            async x => {
                if (x == undefined || x.hasError) {
                    showStatusMessage({
                        message: `${translate(TRANSLATIONS.snackbar.error.ipc.text)}: ${x?.errorMessage}`,
                        autoClose: false,
                        type: 'error'
                    });
                }
            }
        );

        dispatch(toggleLoading());
    };

    const getMainBoundary = useCallback(() => mainRef.current?.getBoundingClientRect(), []);

    const handleFilterReset = async () => {
        dispatch(toggleLoading());

        dispatch(removeTags());

        if (clientMotorResponse) {
            dispatch(setMesRequest(clientMotorResponse.motorRequest));
            await getCalculationData(clientMotorResponse.motorRequest);
            dispatch(toggleUpdateFilters());
        } else {
            await onFilterChange(getDefaultFilter(DEFAULT_FREQUENCY), undefined, undefined, undefined, true);
        }

        dispatch(toggleLoading());
    };

    const handleCreateDocument = async () => {
        if (calculationResponse) {
            const { mesParameters, loadOperationValues } = calculationResponse;
            const { commonData, gearboxData, unitSystem } = mesRequest;

            dispatch(toggleLoading());

            const chartImages = await diagramRef.current?.getDocumentImages();

            const response = await apiCall(
                Api.getDocument({
                    mesParameters,
                    loadOperationValues,
                    gearboxData,
                    chartImages: chartImages ?? INITIAL_CHART_IMAGES,
                    languageCode: Language[language],
                    unitSystem,
                    disabledCurveTypes
                }),
                async x => {
                    const { fileName, data } = x.data;

                    saveDocument(fileName, data);
                },
                async () => {
                    showStatusMessage({
                        message: translate(TRANSLATIONS.snackbar.error.documentGeneration.text),
                        showProgress: true,
                        type: 'error'
                    });
                }
            );

            if (clientMotorResponse) {
                const { motorData, commonData, loadData, gearboxData } = clientMotorResponse.motorRequest;

                track(
                    'Documentation', {
                    motorInfo: {
                        mlfb: motorData.mlfb,
                        frameSize: motorData.frameSize,
                        efficiencyClass: motorData.efficiencyClass,
                        startingVoltage: commonData.startingVoltage,
                        startingType: StartingType[commonData.startingType],
                        numberOfStartupsAccordingToStandardOrNot: commonData.isStartupNumbersChecked,
                        startingCondition: StartingCondition[commonData.startingCondition],
                        loadType: LoadType[loadData.loadType],
                        motorDriveConnection: MotorDriveConnection[gearboxData.motorDriveConnection],
                        loadTorque: gearboxData.loadInertia
                    },
                    success: response.hasError ? false : true
                }
                );
            } else {
                track(
                    'Documentation', {
                    motorInfo: {
                        mlfb: filter.mlfb,
                        frameSize: filter.frameSize,
                        efficiencyClass: filter.efficiencyClass,
                        startingVoltage: mesParameters.inputData.startingVoltage,
                        startingType: StartingType[mesParameters.inputData.startingType],
                        numberOfStartupsAccordingToStandardOrNot: commonData.isStartupNumbersChecked,
                        startingCondition: StartingCondition[commonData.startingCondition],
                        loadType: LoadType[mesParameters.inputData.loadData.loadType],
                        motorDriveConnection: MotorDriveConnection[gearboxData.motorDriveConnection],
                        loadTorque: gearboxData.loadInertia
                    },
                    success: response.hasError ? false : true
                }
                );
            }

            dispatch(toggleLoading());
        }
    };

    const handleVsdSelectChange = async (value: VsdOperationType) => {
        if (calculationResponse) {
            const { isDolOperation, isVsdOperation } = calculationResponse.mesParameters.inputData.vsdMotorData;

            dispatch(setVsdOperationType(value));

            if (isDolOperation && isVsdOperation && value === VsdOperationType.ConverterOperationVSD) {
                dispatch(setDisableOperationalData(true));
                dispatch(setRenderLineOperations(false));
                dispatch(setRenderConverterOperation(true));
            } else if (isDolOperation && isVsdOperation && value === VsdOperationType.LineAndConverterOperationVSD) {
                dispatch(setDisableOperationalData(false));
                dispatch(setRenderLineOperations(true));
                dispatch(setRenderConverterOperation(true));
            } else if (isDolOperation && isVsdOperation && value === VsdOperationType.LineOperation) {
                dispatch(setDisableOperationalData(false));
                dispatch(setRenderLineOperations(true));
                dispatch(setRenderConverterOperation(false));
            }
        }
    };

    const renderLoadInertiaDialog = () => {
        const title = translate(TRANSLATIONS.dialog.info.loadInertiaTooHigh.title);
        const content = translate(TRANSLATIONS.dialog.info.loadInertiaTooHigh.text);
        const buttonText = translate(TRANSLATIONS.dialog.info.loadInertiaTooHigh.button);

        showInfoModal({
            title,
            content,
            buttonText,
            onClose: () => dispatch(setLoadInertiaHighDialog(false))
        });
    };

    const renderNumberOfStartupsDialog = () => {
        const title = translate(TRANSLATIONS.dialog.info.numberOfStartupsValueTooHigh.title);
        const content = translate(TRANSLATIONS.dialog.info.numberOfStartupsValueTooHigh.text);
        const buttonText = translate(TRANSLATIONS.dialog.info.loadInertiaTooHigh.button);

        showInfoModal({
            title,
            content,
            buttonText,
            onClose: () => dispatch(setNumberOfStartupsDialog(false))
        });
    };

    return (
        <HomeContext.Provider value={{ getMainBoundary }}>
            <div className='selector' ref={mainRef}>
                <KeyVisual height={200} url={motorImage} />
                <Loader loading={loading}>
                    <ContentContainer>
                        <SelectorTagBar
                            disabled={disableDocumentCreation}
                            onReset={handleFilterReset}
                            onCreateDocument={handleCreateDocument}
                            backgroundColor={SectionColor.White}
                            showVsdOperationSelect={((isDolOperation && isVsdOperation) ?? false) || ((!isDolOperation && isVsdOperation) ?? false)}
                            onVsdValueChange={handleVsdSelectChange}
                        />
                    </ContentContainer>
                    <Flex justification={FlexJustification.Center} direction={FlexDirection.Column}>
                        {debug && <SpcData />}
                        {(hasMotor || clientMotorResponse) &&
                            <>
                                <div className='operational-data'>
                                    <ContentContainer>
                                        <div className='title'>
                                            <InnoIcon icon='check_checkbox' size='32' theme='light'></InnoIcon>
                                            {`${translate(TRANSLATIONS.filters.operationalData.title)} (${translate((TRANSLATIONS.filters.operationalData as never)[calculationResponse && calculationResponse.isStartupPossible ? 'startUpPossible' : 'startUpNotPossible'])})`}
                                        </div>
                                        <Flex direction={FlexDirection.Row} justification={FlexJustification.Center}>
                                            <StartingConstraint disabled={clientMotorResponse ? disableOperationalData : !hasMotor} />
                                            <LoadData disabled={clientMotorResponse ? disableOperationalData : !hasMotor} />
                                            <GearboxInputData disabled={clientMotorResponse ? disableOperationalData : !hasMotor} />
                                        </Flex>
                                    </ContentContainer>
                                </div>
                                <DiagramCarousel
                                    ref={diagramRef}
                                    tabs={getTabs()}
                                    renderLineOperations={renderLineOperations}
                                    renderConverterOperation={renderConverterOperation}
                                />
                            </>
                        }
                    </Flex>
                </Loader>
                {(clientMotorResponse ? calculationSuccessful : hasMotor) && <Flex justification={FlexJustification.Center} direction={FlexDirection.Column}>
                    <Section color={SectionColor.LightSand} style={{ paddingTop: 15 }}>
                        <ContentContainer>
                            <Result
                                renderLoadOperationDataLine={renderLineOperations}
                                renderLoadOperationDataConverter={renderConverterOperation}
                            />
                        </ContentContainer>
                    </Section>
                </Flex>}
                {debug && calculationResponse && calculationSuccessful && <DebugData />}
            </div>
        </HomeContext.Provider>
    );
};

export default Home;
